import {
  createFileRoute,
  redirect,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useState, type JSX } from "react";
import { z } from "zod";

import { type DocumentType, graphql } from "@repo/graphql-types";
import Left from "@repo/icons/default/Left";
import Trash from "@repo/icons/default/Trash";

import { AppointmentReceivingOptionsForm } from "@/components/appointment/appointment-receiving-options-form";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/ui/drawer/message-drawer";
import { UnexpectedErrorDrawer } from "@/components/ui/drawer/unexpected-error-drawer";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import { PendingComponent } from "@/components/ui/loading/pending-component";
import { queryFn, useGraphQL, useGraphQLMutation } from "@/hooks/use-graphql";

const AppointmentReceivingOptionPageQuery = graphql(/* GraphQL */ `
  query AppointmentReceivingOptionPageQuery($codUsuarioFormaRecebimento: Int = 0) {
    formaRecebimento: usuariosFormasRecebimentos_by_pk(
      codUsuarioFormaRecebimento: $codUsuarioFormaRecebimento
    ) {
      codUsuarioFormaRecebimento
      sistema
    }
    ...AppointmentReceivingOptionsFormQueryFragment
  }
`);

const appointmentReceivingOptionSchema = z.object({
  appointmentReceivingOptionId: z.number().optional(),
  action: z.string().optional(),
});

const DeleteAppointmentReceivingOptionMutation = graphql(`
  mutation DeleteAppointmentReceivingOption($input: Int!) {
    LivanceApiDeleteUsuarioFormaRecebimento(codUsuarioFormaRecebimento: $input)
  }
`);

const AppointmentReceivingOptionPage = (): JSX.Element => {
  const params = Route.useParams();

  const { queryClient } = useRouteContext({ from: "__root__" });

  const router = useRouter();

  const searchParams = useSearch({
    from: "/settings/schedule-options/appointment-receiving-options/$appointmentReceivingOptionId/",
  });

  const queryResult = useGraphQL(AppointmentReceivingOptionPageQuery, {
    codUsuarioFormaRecebimento: Number(params.appointmentReceivingOptionId),
  });

  const { data } = queryResult;

  const openDeleteConfirmationDrawer = (): void => {
    void router.navigate({
      to: "/settings/schedule-options/appointment-receiving-options/$appointmentReceivingOptionId",
      params: {
        appointmentReceivingOptionId: String(params.appointmentReceivingOptionId),
      },
      search: { action: "delete" },
    });
  };

  const closeDeleteConfirmationDrawer = (): void => {
    if (searchParams.action === "delete") {
      router.history.back();
    }
  };

  const [showUnexpectedErrorDrawer, setShowUnexpectedErrorDrawer] = useState(false);

  const { mutateAsync, isPending } = useGraphQLMutation(
    DeleteAppointmentReceivingOptionMutation,
  );

  const onSuccessDelete = (): void => {
    void queryClient.resetQueries({
      queryKey: ["AppointmentReceivingOptionsPageQuery"],
    });
    router.history.go(-2);
  };

  const onErrorDelete = (): void => {
    closeDeleteConfirmationDrawer();
    setShowUnexpectedErrorDrawer(true);
  };

  const onConfirmDelete = async (): Promise<void> => {
    await mutateAsync(
      {
        input: Number(params.appointmentReceivingOptionId),
      },
      {
        onSuccess: onSuccessDelete,
        onError: onErrorDelete,
      },
    );
  };

  const enableDelete = data && !data.formaRecebimento?.sistema;

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Editar convênio" align="center" />
        {enableDelete ? (
          <HeaderButton icon={Trash} onClick={openDeleteConfirmationDrawer} align="end" />
        ) : (
          <div />
        )}
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data && queryResult.isFetchedAfterMount ? (
            <AppointmentReceivingOptionsForm data={data} />
          ) : (
            <FullPageSpinner />
          )}
        </AsyncDataWrapper>
        <MessageDrawerRoot
          icon={Trash}
          variant="secondary"
          open={searchParams.action === "delete"}
          setOpen={closeDeleteConfirmationDrawer}
        >
          <MessageDrawerTitle>Excluir convênio</MessageDrawerTitle>
          <MessageDrawerBody>
            Tem certeza de que deseja excluir este convênio?
          </MessageDrawerBody>
          <MessageDrawerActions>
            <MessageDrawerActionButton
              onClick={() => void onConfirmDelete()}
              loading={isPending}
            >
              Excluir
            </MessageDrawerActionButton>
          </MessageDrawerActions>
        </MessageDrawerRoot>
        <UnexpectedErrorDrawer
          open={showUnexpectedErrorDrawer}
          setOpen={setShowUnexpectedErrorDrawer}
        />
      </Page>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/schedule-options/appointment-receiving-options/$appointmentReceivingOptionId/",
)({
  loader: async (opts) => {
    const data = await opts.context.queryClient.ensureQueryData<
      DocumentType<typeof AppointmentReceivingOptionPageQuery>
    >({
      queryKey: [
        "AppointmentReceivingOptionPageQuery",
        {
          codUsuarioFormaRecebimento: Number(opts.params.appointmentReceivingOptionId),
        },
      ],
      queryFn: () =>
        queryFn(AppointmentReceivingOptionPageQuery, opts.context.authClient, {
          codUsuarioFormaRecebimento: Number(opts.params.appointmentReceivingOptionId),
        }),
    });

    if (!data.formaRecebimento) {
      redirect({
        to: "/settings/schedule-options/appointment-receiving-options",
        throw: true,
      });
    }
  },
  component: AppointmentReceivingOptionPage,
  pendingComponent: () => <PendingComponent title="Editar convênio" />,
  validateSearch: (search) => appointmentReceivingOptionSchema.parse(search),
});
